import {
  Badge,
  Button,
  Combobox,
  DataTable,
  type DataTableColumn,
  Modal,
} from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import {
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { TeamForm } from "./team-form";

type Team = {
  id: string;
  numericId: number;
  yardId: number;
  contractId: number;
  name: string;
  description: string | null;
  delaiPourVisaDays: number | null;
  createdAt: string;
  memberCount: number;
  yard: {
    id: number;
    name: string;
    moaCompanyId: number | null;
    moaCompany: {
      id: number;
      name: string;
    } | null;
  };
  contract: {
    id: number;
    performer: {
      id: number;
      name: string;
    };
  };
};

export function TeamsList() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | undefined>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterMoaCompany, setFilterMoaCompany] = useState<string[]>([]);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    const { data, error } = await apiClient.GET("/api/admin/teams");
    if (error) {
      setError("Erreur lors du chargement des équipes");
      setLoading(false);
      return;
    }
    setTeams(
      data.data.teams.map((t) => ({
        ...t,
        id: String(t.id),
        numericId: t.id,
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleCreate = () => {
    setEditingTeam(undefined);
    setDrawerOpen(true);
  };

  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setDrawerOpen(true);
  };

  const handleDelete = (team: Team) => {
    setDeletingTeam(team);
    setDeleteModalOpen(true);
  };

  const handleViewDetail = (team: Team) => {
    navigate(`/admin/teams/${team.numericId}`);
  };

  const confirmDelete = async () => {
    if (isNil(deletingTeam)) return;

    setIsDeleting(true);
    const { error } = await apiClient.DELETE("/api/admin/teams/{id}", {
      params: { path: { id: deletingTeam.numericId } },
    });

    if (error) {
      toaster.error({
        title: "Erreur",
        description: error.error || "Échec de la suppression de l'équipe",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Équipe supprimée avec succès",
    });

    setDeleteModalOpen(false);
    setDeletingTeam(null);
    setIsDeleting(false);
    fetchTeams();
  };

  const handleFormSuccess = () => {
    fetchTeams();
  };

  const moaCompanyOptions = useMemo(() => {
    const unique = new Map<number, string>();
    for (const team of teams) {
      if (team.yard.moaCompany) {
        unique.set(team.yard.moaCompany.id, team.yard.moaCompany.name);
      }
    }
    return [...unique.entries()]
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([id, name]) => ({ label: name, value: String(id) }));
  }, [teams]);

  const hasActiveFilters = filterMoaCompany.length > 0;

  const clearAllFilters = () => {
    setFilterMoaCompany([]);
  };

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      if (filterMoaCompany.length > 0) {
        if (
          !team.yard.moaCompany ||
          !filterMoaCompany.includes(String(team.yard.moaCompany.id))
        ) {
          return false;
        }
      }
      return true;
    });
  }, [teams, filterMoaCompany]);

  const columns: DataTableColumn<Team>[] = [
    {
      id: "name",
      header: "Nom",
      accessor: (row) => row.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "yard",
      header: "Chantier",
      accessor: (row) => row.yard.name,
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "moaCompany",
      header: "MOA",
      accessor: (row) => row.yard.moaCompany?.name ?? "—",
      isSortable: true,
      loadingCell: "text",
    },
    {
      id: "performer",
      header: "Entreprise",
      accessor: (row) => row.contract.performer.name,
      loadingCell: "text",
    },
    {
      id: "memberCount",
      header: "Membres",
      accessor: (row) => String(row.memberCount),
      cell: ({ row }) => (
        <Badge variant="blue">
          {row.memberCount} membre{row.memberCount !== 1 ? "s" : ""}
        </Badge>
      ),
      loadingCell: "badge",
    },
    {
      id: "actions",
      header: "Actions",
      accessor: () => "",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            plain
            onClick={() => handleViewDetail(row)}
            aria-label="Voir les détails"
          >
            <UsersIcon className="size-4" />
          </Button>
          <Button plain onClick={() => handleEdit(row)} aria-label="Modifier">
            <PencilIcon className="size-4" />
          </Button>
          <Button
            plain
            onClick={() => handleDelete(row)}
            aria-label="Supprimer"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Équipes
          </h2>
          <Button onClick={handleCreate}>
            <PlusIcon data-slot="icon" className="size-4" />
            Créer une équipe
          </Button>
        </div>

        <div className="mb-4 flex items-end gap-3">
          <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Combobox
              options={moaCompanyOptions}
              value={filterMoaCompany}
              onValueChange={({ value }) => setFilterMoaCompany(value)}
              placeholder="Tous les MOA"
              label="MOA"
            />
          </div>
          <Button
            plain
            onClick={clearAllFilters}
            disabled={!hasActiveFilters}
            aria-label="Réinitialiser les filtres"
          >
            <XIcon data-slot="icon" />
            Réinitialiser
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={filteredTeams}
          isLoading={loading}
          isEmpty={filteredTeams.length === 0}
          emptyState={
            <div className="flex min-h-[200px] items-center justify-center text-gray-500">
              Aucune équipe trouvée
            </div>
          }
        />
      </div>

      <TeamForm
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        team={
          editingTeam
            ? {
                id: editingTeam.numericId,
                yardId: editingTeam.yardId,
                contractId: editingTeam.contractId,
                name: editingTeam.name,
                description: editingTeam.description,
                delaiPourVisaDays: editingTeam.delaiPourVisaDays,
              }
            : undefined
        }
        onSuccess={handleFormSuccess}
      />

      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir supprimer l'équipe "
                  {deletingTeam?.name ?? ""}" ? Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type TeamsListProps = Record<string, never>;
